/**
 * CodeInput — segmented one-character-per-box entry for the signup verification
 * code (4 boxes by default). Auto-advances, supports paste/backspace/arrows, and
 * fires onComplete once every box is filled (so the parent can auto-submit).
 *
 * Uncontrolled internally; bump `clearSignal` to reset all boxes (on a wrong
 * code or a resend).
 */

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface CodeInputProps {
  length?: number;
  onComplete: (code: string) => void;
  onChange?: (code: string) => void;
  disabled?: boolean;
  invalid?: boolean;
  /** Change this value to clear the boxes and refocus the first one. */
  clearSignal?: number;
}

const sanitize = (s: string) => s.toUpperCase().replace(/[^A-Z0-9]/g, '');

export const CodeInput: React.FC<CodeInputProps> = ({
  length = 4, onComplete, onChange, disabled, invalid, clearSignal = 0,
}) => {
  const { t } = useTranslation('auth');
  const [digits, setDigits] = useState<string[]>(() => Array(length).fill(''));
  const refs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    setDigits(Array(length).fill(''));
    const timer = setTimeout(() => refs.current[0]?.focus(), 0);
    return () => clearTimeout(timer);
  }, [clearSignal, length]);

  const emit = (next: string[]) => {
    setDigits(next);
    const code = next.join('');
    onChange?.(code);
    if (next.every((d) => d !== '')) onComplete(code);
  };

  const handleChange = (i: number, raw: string) => {
    const s = sanitize(raw);
    const next = [...digits];
    if (s === '') { next[i] = ''; emit(next); return; }
    next[i] = s[s.length - 1]; // last typed char wins
    if (i < length - 1) refs.current[i + 1]?.focus();
    emit(next);
  };

  const handleKeyDown = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const next = [...digits];
      if (next[i]) { next[i] = ''; emit(next); }
      else if (i > 0) { next[i - 1] = ''; emit(next); refs.current[i - 1]?.focus(); }
    } else if (e.key === 'ArrowLeft' && i > 0) {
      refs.current[i - 1]?.focus();
    } else if (e.key === 'ArrowRight' && i < length - 1) {
      refs.current[i + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const s = sanitize(e.clipboardData.getData('text')).slice(0, length);
    if (!s) return;
    const next = Array(length).fill('').map((_, i) => s[i] ?? '');
    refs.current[Math.min(s.length, length - 1)]?.focus();
    emit(next);
  };

  return (
    <div className="d-flex justify-content-center gap-2" onPaste={handlePaste}>
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          ref={(el) => { refs.current[i] = el; }}
          type="text" inputMode="text" autoComplete="one-time-code" maxLength={1}
          aria-label={t('code.characterAria', { number: i + 1 })}
          className="form-control text-center fw-bold p-0"
          style={{
            width: 52, height: 62, fontSize: 26, textTransform: 'uppercase',
            borderColor: invalid ? '#dc3545' : undefined,
            boxShadow: invalid ? '0 0 0 .15rem rgba(220,53,69,.2)' : undefined,
          }}
          value={digits[i] ?? ''}
          disabled={disabled}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onFocus={(e) => e.target.select()}
        />
      ))}
    </div>
  );
};
